<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="px-3">
          <div class="card bg-white border-0 rounded-3 mb-4">
             <div class="card-body p-4">
                        <!-- Desktop version -->
              <ul
                class="nav nav-tabs justify-content-between border-0 mb-0 wizard-tabs d-none d-md-flex"
                id="myTabstep"
                role="tablist"
              >
                <li 
                  v-for="(section, index) in sections" 
                  :key="section.id"
                  class="nav-item text-center" 
                  role="presentation"
                >
                  <button
                    :class="['nav-link', 'wh-48', 'bg-primary', 'bg-opacity-10', 'rounded-circle', 'p-0', 'd-block', 'mx-auto', 'mb-2', { 'active': activeStepId === section.id }]"
                    :id="`${section.id}-step-tab`"
                    data-bs-toggle="tab"
                    :data-bs-target="`#${section.id}-pane`"
                    type="button"
                    role="tab"
                    :aria-controls="`${section.id}-pane`"
                    :aria-selected="activeStepId === section.id"
                    @click="setStep(section.id)"
                  >
                    <span class="fs-20 fw-bold text-primary">{{ index + 1 }}</span>
                  </button>
                  <div class="text-center">
                    <small class="text-muted fw-medium">{{ getSectionTitle(section) }}</small>
                  </div>
                </li>
              </ul>

              <!-- Mobile version -->
              <ul
                class="nav nav-tabs border-0 mb-0 wizard-tabs-mobile d-block d-md-none"
                id="myTabstepMobile"
                role="tablist"
              >
                <li 
                  v-for="(section, index) in sections" 
                  :key="section.id"
                  class="nav-item mb-1" 
                  role="presentation"
                >
                  <button
                    :class="['nav-link', 'd-flex', 'align-items-center', 'p-2', 'text-start', 'w-100', 'border-0', { 'active': activeStepId === section.id }]"
                    :id="`${section.id}-step-tab-mobile`"
                    data-bs-toggle="tab"
                    :data-bs-target="`#${section.id}-pane`"
                    type="button"
                    role="tab"
                    :aria-controls="`${section.id}-pane`"
                    :aria-selected="activeStepId === section.id"
                    @click="setStep(section.id)"
                  >
                    <div
                      :class="['rounded-circle', 'd-flex', 'align-items-center', 'justify-content-center', 'me-2', 'flex-shrink-0', { 'bg-primary text-white': activeStepId === section.id, 'bg-white text-primary border border-2 border-primary': activeStepId !== section.id }]"
                      style="width: 24px; height: 24px;"
                    >
                      <span class="fw-bold" style="font-size: 12px;">{{ index + 1 }}</span>
                    </div>
                    <div class="flex-grow-1">
                      <div :class="['fw-medium', { 'text-primary': activeStepId === section.id, 'text-dark': activeStepId !== section.id }]" style="font-size: 14px;">
                        {{ getSectionTitle(section) }}
                      </div>
                    </div>
                  </button>
                </li>
              </ul>
          </div></div>
</div>

      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div v-if="showAlert" class="alert alert-warning text-warning" role="alert">
            <i class="ri-error-warning-line fs-18 me-1"></i>
            <span v-html="alertMessage"></span>
          </div>
          <div class="tab-content" id="myTabstep2Content">
            <div
              v-for="section in sections"
              :key="section.id"
              class="tab-pane fade"
              :class="{ 'show active': activeStepId === section.id }"
              :id="`${section.id}-pane`"
              role="tabpanel"
              :aria-labelledby="`${section.id}-tab`"
              tabindex="0"
            >
              <h4 class="mb-4">{{ getSectionTitle(section) }}</h4>
              <p class="text-gray-light mb-0">
                    {{ getSectionDescription(section) }}
                  </p>
                  <hr>

              <FormField
                v-for="field in visibleFieldsForSection(section)"
                :key="field.name"
                :field="field"
                v-model="formData[field.name]"
                @option-selected="field.name === 'category' && handleCategoryChange($event)"
                :show-error="formValidationState[field.name] === false && (showGlobalErrors || (activeStepId === section.id && touched))"
                :error-message="formValidationErrors[field.name] || (field.validation && field.validation.errorMessage ? field.validation.errorMessage : 'Questo campo è obbligatorio')"
              />

              <div class="mt-4 d-flex" :class="isFirstStep ? 'justify-content-end' : 'justify-content-between'">
                <button 
                  v-if="!isFirstStep"
                  class="btn btn-primary bg-primary bg-opacity-10 text-primary py-3 px-5 fw-semibold border-0"
                  @click="prevStep"
                 
                ><!--  :disabled="isFirstStep" -->
                  Indietro
                </button>
                <button 
                  class="btn btn-primary py-3 px-5 fw-semibold text-white"
                  @click="nextStep"
                >

                <!--  :disabled="!isStepValid" -->
                  {{ isLastStep ? schema.formSchema.submitButton.text : 'Avanti' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import FormField from '@/components/FormInputs/FormField.vue';
import schema from '../../../../../json-input/forfettario-calculator-input-schema.json';
import { evaluateCondition, calculateYearsActive, isFirstYear, isProfessionalCasse, isInpsCasse } from '@/utils/wizardHelpers';
import { t, setTranslations } from '@/utils/i18nHelper';
import { onMounted } from 'vue';

// Reactive state for the wizard
const sections = ref(schema.formSchema.sections);
const activeStepId = ref(sections.value.length > 0 ? sections.value[0].id : null);

const showAlert = ref(false);
const alertMessage = ref('');

const emit = defineEmits(['submit-wizard']);

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({}),
  },
});

// --- Form Data State ---
const formData = ref<Record<string, any>>({});

// Initialize formData with initialData if provided, otherwise with default values from the schema
if (Object.keys(props.initialData).length > 0) {
  formData.value = { ...props.initialData };
} else {
  schema.formSchema.sections.forEach(section => {
    section.fields.forEach(field => {
      // Set a default value based on type
      if (field.type === 'switch') {
        formData.value[field.name] = false;
      } else if (field.type === 'currency') {
        formData.value[field.name] = null;
      } else {
        // Check for a default value in validation schema
        if (field.validation && 'default' in field.validation) {
          formData.value[field.name] = field.validation.default;
        } else if (field.type === 'select') {
          formData.value[field.name] = '';
        } else {
          formData.value[field.name] = null;
        }
      }
    });
  });
}

// Helper functions object to pass to evaluateCondition
const wizardHelperFunctions = {
  calculateYearsActive,
  isFirstYear,
  isProfessionalCasse,
  isInpsCasse,
};

// Computed property to determine field visibility
const visibleFieldsForSection = computed(() => (section: any) => {
  return section.fields
    .filter((field: any) => {
      if (!field.conditionalDisplay) {
        return true; // No condition, always visible
      }

      const result = evaluateCondition(
        field.conditionalDisplay.condition,
        formData.value,
        wizardHelperFunctions
      );

      if (field.conditionalDisplay.type === 'include') {
        return result;
      } else if (field.conditionalDisplay.type === 'exclude') {
        return !result;
      }
      return true; // Default to visible if type is unknown
    })
    .map((field: any) => {
      if (field.type === 'date') {
        let maxDate = new Date(); // Default maxDate is today
        if (field.name === 'dataNascita') {
          const eighteenYearsAgo = new Date();
          eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
          maxDate = eighteenYearsAgo;
        }
        return { ...field, maxDate: maxDate };
      }
      return field;
    });
});

// --- Validation ---
const touched = ref(false);
const formValidationState = ref<Record<string, boolean>>({});
const formValidationErrors = ref<Record<string, string>>({});
const showGlobalErrors = ref(false);

// Initialize formValidationState
schema.formSchema.sections.forEach(section => {
  section.fields.forEach(field => {
    formValidationState.value[field.name] = true; // Assume valid initially
    formValidationErrors.value[field.name] = ''; // Initialize error messages
  });
});

const currentSection = computed(() => {
  return sections.value.find(s => s.id === activeStepId.value) || { fields: [] };
});

const isFieldValueValid = (field: any) => {
  const value = formData.value[field.name];
  const validation = field.validation;

  // Special validation for ENPAM birth date
  if (field.name === 'dataNascita' && formData.value.contributionRegime === 'ENPAM_MEDICI') {
    if (!value) {
      formValidationState.value[field.name] = false;
      formValidationErrors.value[field.name] = translations.value['ff.validation.dataNascita.required'] || 'Inserisci la data di nascita';
      return false;
    }
    const birthDate = new Date(value);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    if (age < 18 || age > 80) {
        formValidationState.value[field.name] = false;
        formValidationErrors.value[field.name] = translations.value['ff.validation.dataNascita.age_range'] || "L'età deve essere compresa tra 18 e 80 anni";
        return false;
    }
    // If validation passes, clear any previous error
    formValidationErrors.value[field.name] = '';
  }

  // Rule 1: Required check
  if (field.required) {
    if (value === null || value === undefined || value === '') {
      formValidationState.value[field.name] = false;
      formValidationErrors.value[field.name] = field.validation?.errorMessage || 'Questo campo è obbligatorio';
      return false;
    }
  }

  // Rule 2: If not required and empty, the field is valid.
  if (!field.required && (value === null || value === undefined || value === '')) {
    formValidationState.value[field.name] = true;
    return true;
  }

  // Rule 3: If there is a value, apply detailed validation rules.
  if (validation && value !== null && value !== undefined && value !== '') {
    let isValid = true;
    const numericValue = parseFloat(value);

    switch (validation.type) {
      case 'number':
        if (isNaN(numericValue)) {
          isValid = false;
          break;
        }
        if (validation.min !== undefined && numericValue < validation.min) {
          isValid = false;
        }
        if (validation.max !== undefined && numericValue > validation.max) {
          isValid = false;
        }
        break;

      case 'string':
        if (validation.minLength !== undefined && String(value).length < validation.minLength) {
          isValid = false;
        }
        break;
    }

    formValidationState.value[field.name] = isValid;
    if (!isValid) {
      formValidationErrors.value[field.name] = validation.errorMessage || 'Valore non valido';
    } else {
      formValidationErrors.value[field.name] = '';
    }
    return isValid;
  }

  // Default to valid if no specific rules fail
  formValidationState.value[field.name] = true;
  formValidationErrors.value[field.name] = '';
  return true;
};

const validateCurrentStep = () => {
  // Only validate fields that are currently visible in the current section
  return visibleFieldsForSection.value(currentSection.value).every(field => isFieldValueValid(field));
};

const validateAllFields = () => {
  let allValid = true;
  sections.value.forEach(section => {
    // Only validate fields that are currently visible in each section
    visibleFieldsForSection.value(section).forEach(field => {
      if (!isFieldValueValid(field)) {
        allValid = false;
      }
    });
  });
  return allValid;
};

const handleCategoryChange = (selectedOption: any) => {
  if (selectedOption?.other_data?.autoSelectRegime) {
    const autoSelectedRegime = selectedOption.other_data.autoSelectRegime;
    if (COMING_SOON_REGIMES.includes(autoSelectedRegime)) {
      formData.value.contributionRegime = 'GESTIONE_SEPARATA';
    } else {
      formData.value.contributionRegime = autoSelectedRegime;
    }
  }
};

const clearHiddenFieldValues = () => {
  sections.value.forEach(section => {
    section.fields.forEach(field => {
      if (field.conditionalDisplay) {
        const shouldBeVisible = evaluateCondition(
          field.conditionalDisplay.condition,
          formData.value,
          wizardHelperFunctions
        );

        let isCurrentlyVisible = false;
        if (field.conditionalDisplay.type === 'include') {
          isCurrentlyVisible = shouldBeVisible;
        } else if (field.conditionalDisplay.type === 'exclude') {
          isCurrentlyVisible = !shouldBeVisible;
        }

        if (!isCurrentlyVisible && formData.value[field.name] !== null && formData.value[field.name] !== undefined && formData.value[field.name] !== '') {
          // Clear the value if the field is hidden and has a value
          if (field.type === 'switch') {
            formData.value[field.name] = false;
          } else if (field.type === 'currency' || field.type === 'number') {
            formData.value[field.name] = null; // Or 0, depending on desired default for numbers
          } else {
            formData.value[field.name] = ''; // Default for text, select, date, etc.
          }
        }
      }
    });
  });
};

// Watch for changes in startDate and clear dependent fields
watch(() => formData.value.startDate, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    clearHiddenFieldValues();
  }
});

const COMING_SOON_REGIMES = [
// TODO
  'GESTIONE_SEPARATA_EX_INPGI',
  'IVS_COMMERCIANTI_ENASARCO',
];

const COMING_SOON_CATEGORIES = [
  'GIORNALISTA',
  'INTERMEDIARIES',
];

watch([() => formData.value.contributionRegime, () => formData.value.category], ([newRegime, newCategory]) => {
  if (COMING_SOON_REGIMES.includes(newRegime) || COMING_SOON_CATEGORIES.includes(newCategory)) {
    alertMessage.value = '<strong>Attenzione!</strong> Il regime contributivo o la categoria selezionata è "presto disponibile". Il calcolo potrebbe non essere preciso. Verrai contattato quando sarà ultimato.';
    showAlert.value = false;
  } else {
    showAlert.value = false;
    alertMessage.value = '';
  }
});

// --- Navigation ---
const activeIndex = computed(() => sections.value.findIndex(s => s.id === activeStepId.value));
const isFirstStep = computed(() => activeIndex.value === 0);
const isLastStep = computed(() => activeIndex.value === sections.value.length - 1);

const setStep = (id: string) => {
  activeStepId.value = id;
  touched.value = false; // Reset touched state on step change
};

const nextStep = () => {
  touched.value = true;
  showGlobalErrors.value = false; // Reset global errors on next step attempt

  clearHiddenFieldValues(); // Clear values of hidden fields before validation

  if (validateCurrentStep()) {
    if (isLastStep.value) {
      if (validateAllFields()) {
                  // Temporary fix: Ensure fatturatoPrecedente is 0 if not present
          if (formData.value.fatturatoPrecedente === null || formData.value.fatturatoPrecedente === undefined || formData.value.fatturatoPrecedente === '') {
            formData.value.fatturatoPrecedente = 0;
          }
          emit('submit-wizard', formData.value);
      } else {
        showGlobalErrors.value = true;
        // Find the first invalid field and navigate to its section
        let firstInvalidSectionId: string | null = null;
        for (const section of sections.value) {
          for (const field of section.fields) {
            if (!formValidationState.value[field.name]) {
              firstInvalidSectionId = section.id;
              break;
            }
          }
          if (firstInvalidSectionId) {
            break;
          }
        }
        if (firstInvalidSectionId) {
          setStep(firstInvalidSectionId);
        }
      }
    } else {
      const nextSection = sections.value[activeIndex.value + 1];
      if (nextSection) {
        setStep(nextSection.id);
      }
    }
  }
};

const prevStep = () => {
  if (!isFirstStep.value) {
    const prevSection = sections.value[activeIndex.value - 1];
    if (prevSection) {
      setStep(prevSection.id);
    }
  }
};

// Reactive translations store
const translations = ref<Record<string, string>>({});

// CSV parser helper function
const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current);
  return result;
};

// --- Helpers ---
const getSectionTitle = (section: any) => {
  const currentYear = new Date().getFullYear();
  
  // Use reactive translations with fallback to original title
  const titleKey = `ff.sections.${section.id}.title`;
  const fallbackTitle = section.title.replace('{CURRENT_YEAR-1}', String(currentYear - 1));
  
  // Check reactive translations first, then fallback
  const translatedTitle = translations.value[titleKey] || fallbackTitle;
  
  // Replace placeholders
  return translatedTitle.replace(/\{(\w+)\}/g, (match, key) => {
    const params = { 'CURRENT_YEAR-1': currentYear - 1 };
    return params[key as keyof typeof params] !== undefined ? params[key as keyof typeof params].toString() : match;
  });
};

const getSectionDescription = (section: any) => {
  const currentYear = new Date().getFullYear();
  
  // Use reactive translations with fallback to original description  
  const descriptionKey = `ff.sections.${section.id}.description`;
  const fallbackDescription = section.description.replace('{CURRENT_YEAR-1}', String(currentYear - 1));
  
  // Check reactive translations first, then fallback
  const translatedDescription = translations.value[descriptionKey] || fallbackDescription;
  
  // Replace placeholders
  return translatedDescription.replace(/\{(\w+)\}/g, (match, key) => {
    const params = { 'CURRENT_YEAR-1': currentYear - 1 };
    return params[key as keyof typeof params] !== undefined ? params[key as keyof typeof params].toString() : match;
  });
};

// Function to parse CSV and load translations
const loadTranslationsFromCSV = async () => {
  try {
    // Fetch the CSV from public folder
    const response = await fetch('/forfettario-i18n-keys.csv');
    if (!response.ok) {
      throw new Error(`Failed to fetch CSV: ${response.statusText}`);
    }
    const csvText = await response.text();
    
    // Parse CSV manually (robust parser for quoted fields)
    const lines = csvText.split('\n');
    
    // Parse first line to get headers
    const headers = parseCSVLine(lines[0]);
    const keyIndex = headers.indexOf('key');
    const textIndex = headers.indexOf('testo_attuale');
    
    if (keyIndex === -1 || textIndex === -1) {
      console.error('CSV format invalid: missing key or testo_attuale columns');
      return;
    }
    
    const parsedTranslations: Record<string, string> = {};
    const currentYear = new Date().getFullYear();

    // Process each line (skip header)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const columns = parseCSVLine(line);
      const key = columns[keyIndex]?.trim();
      let text = columns[textIndex]?.trim();

      if (key && text) {
        // Replace placeholders in the text
        text = text.replace('{CURRENT_YEAR-1}', String(currentYear - 1));
        parsedTranslations[key] = text;
      }
    }
    
    console.log('Loaded translations from CSV:', parsedTranslations);
    
    // Set reactive translations
    translations.value = parsedTranslations;
    
    // Also set in global helper for compatibility
    setTranslations(parsedTranslations);
    
  } catch (error) {
    console.error('Failed to load CSV translations:', error);
  }
};

// Load translations from CSV on mount
onMounted(() => {
  loadTranslationsFromCSV();
});

</script>

<style scoped>
.wizard-tabs-mobile .nav-link {
  border-radius: 8px !important;
  transition: all 0.3s ease;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

.wizard-tabs-mobile .nav-link:hover {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

.wizard-tabs-mobile .nav-link.active {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

@media (max-width: 767.98px) {
  .wizard-tabs {
    display: none !important;
  }
  
  .wizard-tabs-mobile {
    display: block !important;
  }
}
</style>